/**
 * Sacar una imagen del portapapeles.
 *
 * ⚠ **El nombre se deriva del MIME, no del que trae el navegador.** Chrome llama
 * `image.png` a todo lo que copia, incluido un JPEG copiado de una página web. El
 * servidor deduce la extensión declarada del nombre y luego comprueba los bytes
 * (regla 7): con un JPEG llamado `.png`, la comprobación falla y quien pegó recibe
 * `ARCHIVO_INVALIDO` sobre una captura perfectamente válida. Nombrándolo por su tipo
 * real, las dos cosas coinciden y la comprobación de bytes sigue siendo la que manda.
 *
 * ⚠ **Sólo los tres formatos que el almacén admite.** SVG queda fuera —es un vector
 * de XSS, y el servidor lo rechaza igual—, y aceptarlo aquí sólo serviría para
 * enseñar una vista previa de algo que va a fallar al enviarse.
 */

/** MIME → extensión, alineado con `EXTENSION_POR_MIME` del backend. */
const EXTENSION: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
}

/**
 * La imagen de un evento de pegado, o `null` si no había ninguna.
 *
 * ⚠ **`null` cuando se pega texto, y eso es lo que hace seguro tener el oyente
 * puesto.** Pegar en un campo de texto no toca el adjunto: aquí sólo se mira si el
 * portapapeles trae un archivo de imagen.
 */
export function imagenDelPortapapeles(datos: DataTransfer | null): File | null {
  if (datos === null) return null

  for (const elemento of Array.from(datos.items)) {
    if (elemento.kind !== "file") continue
    const archivo = elemento.getAsFile()
    if (archivo === null) continue
    const extension = EXTENSION[archivo.type]
    if (extension === undefined) continue
    // Nombre nuestro, derivado del tipo. El original ni se mira: ver la cabecera.
    return new File([archivo], `captura.${extension}`, { type: archivo.type })
  }
  return null
}

/** `true` si el portapapeles trae algo que este sistema aceptaría como captura. */
export function traeImagen(datos: DataTransfer | null): boolean {
  return imagenDelPortapapeles(datos) !== null
}
